import React from 'react';
import {Chip, Link, Stack, Typography} from '@mui/material';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import useSourcesFileViewerStore from '../../../stores/useSourcesFileViewerStore';
import useSourcePackageBuilderStore from '../../../stores/useSourcePackageBuilderStore';

const KIND_LABELS = {
    source_package: 'Source Package',
    data_item: 'Data Item',
    corpus: 'Corpus',
};

/** Mirrors `useOpenSearchHit.js`'s `documentTypeToFileType` — duplicated here (rather than
 * imported across the core-editor/feature boundary) since it's a tiny, static lookup. */
function documentTypeToFileType(documentType) {
    switch (documentType) {
        case 'PDF': return 'pdf';
        case 'AUDIO': return 'audio';
        case 'VIDEO': return 'video';
        case 'IMAGE': return 'image';
        default: return 'unknown';
    }
}

/**
 * MINSKY-16823 (ticket 04): open the data item this in-document citation mark references,
 * reusing the same `SourcesFileViewerModal` / `useSourcesFileViewerStore` mechanism as the
 * Forecast panel preview's popover (ticket 03).
 *
 * `referenceId` is preferred (the viewer's personal REFERENCE copy of the file). When it is
 * null (the user has no personal copy in My Files — corpus-only hit), we fall back to
 * `canonicalFileId` so that citations to shared corpus documents are still openable.
 */
export function openCitationRefDataItem(attrs) {
    if (!attrs || attrs.kind !== 'data_item') return;
    const fileId = attrs.referenceId ?? attrs.canonicalFileId;
    if (!fileId) return;
    useSourcesFileViewerStore.getState().open({
        fileId,
        canonicalFileId: attrs.canonicalFileId,
        fileName: attrs.title,
        fileType: documentTypeToFileType(attrs.documentType),
        sentenceHit: attrs.sentenceHit || null,
    });
}

/**
 * MINSKY-16821 gap fix: open the corpus document this in-document citation mark references,
 * reusing the same `SourcePackageBuilder` / `useSourcePackageBuilderStore` "corpus" view as the
 * Forecast panel preview's popover — mirrors `openCorpusSource` in `ForecastCitations.jsx`.
 * Only `corpus` marks with a `docId` are openable.
 */
export function openCitationRefCorpusDocument(attrs) {
    if (!attrs || attrs.kind !== 'corpus' || attrs.docId == null) return;
    useSourcePackageBuilderStore.getState().open({
        viewKind: 'corpus',
        documentId: String(attrs.docId),
        segmentIndex: attrs.segmentIndex ?? null,
    });
}

/**
 * MINSKY-16818 (ticket 01): open the source-package document this in-document citation mark
 * references. A `source_package` source (the report's own uploaded Sources-tab document) and a
 * `corpus` source (a federated-search hit) both reference the exact same `documents`/`segments`
 * tables — a source-package item IS a {@code Document} the report included, and a corpus hit is
 * the same {@code Document}/{@code Segment} entities found via search — so this reuses the
 * identical `SourcePackageBuilder` "corpus" document view via {@code docId}/{@code segmentIndex}.
 * Only `source_package` marks with a `docId` are openable.
 */
export function openCitationRefSourcePackage(attrs) {
    if (!attrs || attrs.kind !== 'source_package' || attrs.docId == null) return;
    useSourcePackageBuilderStore.getState().open({
        viewKind: 'corpus',
        documentId: String(attrs.docId),
        segmentIndex: attrs.segmentIndex ?? null,
    });
}

/**
 * MINSKY-16818 (ticket 02): dispatch to the right viewer's open() based on a citation source's
 * kind — the single shared place for this kind-branching, reused by both the in-document
 * citation popover (`citationRefPopoverPlugin.jsx`) and the Forecast panel's per-item sources
 * list + clickable inline markers, so neither reimplements it.
 */
export function openCitationRefSource(attrs) {
    if (!attrs) return;
    if (attrs.kind === 'data_item') {
        openCitationRefDataItem(attrs);
    } else if (attrs.kind === 'corpus') {
        openCitationRefCorpusDocument(attrs);
    } else if (attrs.kind === 'source_package') {
        openCitationRefSourcePackage(attrs);
    }
}

/** Whether a citation mark's kind + identifiers are sufficient to open some viewer. Exported
 * (ticket 02) so the Forecast panel's per-item sources list + inline markers can gate their own
 * clickable affordance the same way this card gates its "Open source" link.
 *
 * For `data_item` sources: openable when either `referenceId` (personal REFERENCE copy) or
 * `canonicalFileId` (corpus entry without a personal copy) is present. */
export function isMarkOpenable(attrs) {
    if (!attrs) return false;
    if (attrs.kind === 'data_item') return Boolean(attrs.referenceId ?? attrs.canonicalFileId);
    if (attrs.kind === 'corpus' || attrs.kind === 'source_package') return attrs.docId != null;
    return false;
}

/**
 * Popover content for an in-document `citationRef` mark. Mirrors
 * `ForecastCitations.jsx`'s `CitationPopoverContent` (ticket 02/03) so the same citation reads
 * and behaves identically whether it's in the Forecast panel preview or already inserted into
 * the report body.
 */
export function CitationRefCard({attrs, onOpenSource}) {
    if (!attrs) return null;
    const isOpenable = isMarkOpenable(attrs);
    return (
        <Stack spacing={0.75} p={1.5}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    [{attrs.number}]
                </Typography>
                <Chip label={KIND_LABELS[attrs.kind] || 'Source'} size="small"/>
            </Stack>
            <Typography variant="body2" fontWeight="medium">
                {attrs.title || 'Untitled source'}
            </Typography>
            {attrs.snippet && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                >
                    {attrs.snippet}
                </Typography>
            )}
            {isOpenable && (
                <Link
                    component="button"
                    type="button"
                    variant="caption"
                    underline="hover"
                    onClick={(e) => {
                        e.stopPropagation();
                        onOpenSource(attrs);
                    }}
                    sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5, alignSelf: 'flex-start'}}
                >
                    <OpenInNewRoundedIcon sx={{fontSize: 14}}/>
                    Open source
                </Link>
            )}
        </Stack>
    );
}
